/**
 * UI-Schicht: Rendering, Event-Binding und die pro Frame laufende
 * HUD-Aktualisierung. Drive ist ein Querformat-Head-up-Display; der zentrale
 * Tacho-Cluster ist gleichzeitig das „Gaspedal" (Tippen startet, Halten = Gas).
 * Der G-Sensor ist nach dem Start dauerhaft aktiv.
 */
import { state, persist, allProfiles, redlineRpm, applyMixPreset } from './state/store.js'
import { FACTORY_PROFILES } from './data/profiles.js'
import { clamp, smooth, $, toast, log } from './util.js'
import {
  ensureContext,
  buildSources,
  updateAudio,
  refreshProfileParams,
  stopSources,
} from './audio/engine.js'
import { eventBoom, unlockHit } from './audio/events.js'
import { buildScenarioFingerprint } from './audio/sound-lab.js'
import { getSoundModel } from './audio/sound-models.js'
import { stepPhysics } from './sim/physics.js'
import { toggleSensor } from './sim/sensor.js'

const SOUND_SCENARIO_LABELS = {
  idle: 'Idle',
  pull: 'Pull',
  overrun: 'Offload',
  shift: 'Shift',
}
let soundCheckScenario = 'idle'
let soundPreviewTimer = 0
let soundPreviewRestore = null

/* ---------------------------------------------------------------- Rendering */

function carSvg(c) {
  return `<svg viewBox="0 0 120 60" aria-hidden="true"><path d="M9 36 C18 21 33 18 47 19 L58 9 C76 10 91 20 105 35 L111 37 L107 44 L13 44 Z" fill="${c}"/><circle cx="31" cy="44" r="10" fill="#050609" stroke="#fff" stroke-width="5"/><circle cx="90" cy="44" r="10" fill="#050609" stroke="#fff" stroke-width="5"/></svg>`
}

function renderCards() {
  const wrap = $('cards')
  wrap.innerHTML = ''
  allProfiles().forEach((p) => {
    const div = document.createElement('div')
    div.className = 'box car'
    div.id = 'card-' + p.id
    div.innerHTML = `
      <div class="art">${carSvg(p.color)}</div>
      <div class="meta">
        <div class="engineLine"><span>${p.flag}</span><b>${p.engineType}</b></div>
        <div class="nm">${p.brand} ${p.name}</div>
        <div class="sp">${p.spec}<br>${p.sub}</div>
        <div class="tags">${p.tags.map((t) => `<span class="tag">${t}</span>`).join('')}</div>
      </div>
      <button class="mini" type="button">WÄHLEN</button>`
    div.onclick = () => selectProfile(p.id)
    wrap.appendChild(div)
  })
}

function renderShiftlights() {
  const w = $('shiftlights')
  if (!w) return
  w.innerHTML = ''
  for (let i = 0; i < 12; i++) w.appendChild(document.createElement('i'))
}

function currentSoundModel() {
  return getSoundModel(state.profile.soundModelId || FACTORY_PROFILES[0].soundModelId)
}

function formatHz(value) {
  return `${Math.round(value)} Hz`
}

function inductionLabel(induction) {
  if (!induction?.type || induction.type === 'none') return 'NA'
  if (induction.type === 'supercharger') return 'KOMPRESSOR'
  return induction.type.toUpperCase()
}

function renderSoundCheck() {
  const root = $('soundCheck')
  if (!root) return

  const model = currentSoundModel()
  const fingerprint = buildScenarioFingerprint(model, soundCheckScenario, {
    sampleRate: 8000,
    durationSeconds: 0.12,
  })
  const engine = model.engine
  const maxEnergy = Math.max(...fingerprint.orders.map((entry) => entry.energy), 0.000001)
  const resonancePeaks = [...fingerprint.resonancePeaks].sort((a, b) => b.gain - a.gain).slice(0, 4)

  set('soundModelName', model.label)
  set(
    'soundModelMeta',
    `${engine.cylinders} ZYL · ${engine.crankType} · ${engine.idleRpm}-${engine.redlineRpm} RPM · ${inductionLabel(fingerprint.induction)}`,
  )
  set('soundRpm', String(fingerprint.rpm))
  set('soundFiring', formatHz(fingerprint.firingHz))
  set('soundCentroid', formatHz(fingerprint.spectralCentroidHz))
  set('soundLowOrder', Math.round(fingerprint.lowOrderBalance * 100) + '%')

  const orders = $('soundOrders')
  if (orders) {
    orders.innerHTML = fingerprint.orders
      .map((entry) => {
        const width = clamp((entry.energy / maxEnergy) * 100, 4, 100)
        return `<div class="orderBar">
          <span>${entry.order}x</span>
          <i><b style="width:${width.toFixed(1)}%"></b></i>
          <em>${formatHz(entry.frequencyHz)}</em>
        </div>`
      })
      .join('')
  }

  const resonances = $('soundResonances')
  if (resonances) {
    resonances.innerHTML = resonancePeaks
      .map((peak) => `<span>${peak.channel} ${formatHz(peak.trackedFrequencyHz)}</span>`)
      .join('')
  }

  document.querySelectorAll('#soundScenarios button').forEach((button) => {
    const on = button.dataset.scenario === soundCheckScenario
    button.classList.toggle('on', on)
    button.setAttribute('aria-pressed', String(on))
  })
}

// Tacho-Geometrie: gemeinsamer Mittelpunkt (146,146), symmetrischer Bereich
// 150°→390° (240° Sweep, Lücke exakt unten). Strich 5 sitzt genau oben (270°).
const CX = 146,
  CY = 146,
  GA_START = 150,
  GA_SWEEP = 240

function renderTicks() {
  const g = $('ticks')
  let s = ''
  for (let i = 0; i <= 10; i++) {
    const a = ((GA_START + i * (GA_SWEEP / 10)) * Math.PI) / 180
    const major = i % 5 === 0
    const r1 = 100,
      r2 = major ? 84 : 91
    const x1 = CX + Math.cos(a) * r1,
      y1 = CY + Math.sin(a) * r1
    const x2 = CX + Math.cos(a) * r2,
      y2 = CY + Math.sin(a) * r2
    s += `<line x1="${x1.toFixed(2)}" y1="${y1.toFixed(2)}" x2="${x2.toFixed(2)}" y2="${y2.toFixed(2)}" stroke="${i >= 9 ? '#ffbd54' : '#3c4b5e'}" stroke-width="${major ? 4 : 2}" stroke-linecap="round"/>`
  }
  g.innerHTML = s
}

/* ------------------------------------------------------------- Profilwahl */

function selectProfile(id) {
  const p = allProfiles().find((x) => x.id === id) || FACTORY_PROFILES[0]
  state.profile = p
  state.activeProfileId = p.id
  persist()

  document.querySelectorAll('.car').forEach((c) => c.classList.remove('sel'))
  document.querySelectorAll('.car .mini').forEach((b) => {
    b.classList.remove('on')
    b.textContent = 'WÄHLEN'
  })
  const card = $('card-' + id)
  if (card) {
    card.classList.add('sel')
    const btn = card.querySelector('.mini')
    if (btn) {
      btn.classList.add('on')
      btn.textContent = 'AKTIV'
    }
  }
  // Synthese läuft durchgehend – nur die Motorparameter umstellen.
  if (state.started) refreshProfileParams()
  renderSoundCheck()
}

/* ----------------------------------------------------------- Audio-Start */

async function startAudio() {
  const sensorPromise = state.sensor
    ? Promise.resolve(true)
    : toggleSensor().catch((e) => {
        log.warn('G-Sensor:', e.message)
        return false
      })

  try {
    await ensureContext()
    if (!state.engine) buildSources()
    state.started = true
    state.engine = true
    state.rpm = state.profile.idle || 880
    state.gear = 1
    $('cluster').classList.add('live')
    toast('AUDIO RUNNING', 'G-SENSOR SOUND AKTIV')
    unlockHit()
    await sensorPromise
  } catch (e) {
    toast('AUDIO FEHLER', e.message)
    log.error('startAudio:', e.message)
  }
}

async function previewSoundScenario() {
  if (soundPreviewTimer && soundPreviewRestore) {
    window.clearTimeout(soundPreviewTimer)
    soundPreviewRestore()
  }

  const cluster = $('cluster')
  const saved = {
    started: state.started,
    engine: state.engine,
    clusterLive: cluster.classList.contains('live'),
    rpm: state.rpm,
    speed: state.speed,
    throttle: state.throttle,
    targetThrottle: state.targetThrottle,
    overrun: state.overrun,
    shift: state.shift,
    shiftDir: state.shiftDir,
    manualHold: state.manualHold,
    gear: state.gear,
    load: state.load,
    gPunch: state.gPunch,
    rpmDrop: state.rpmDrop,
    pendingShiftRpm: state.pendingShiftRpm,
    shiftBlend: state.shiftBlend,
    soundPreviewActive: state.soundPreviewActive,
  }
  const model = currentSoundModel()
  const fingerprint = buildScenarioFingerprint(model, soundCheckScenario, {
    sampleRate: 8000,
    durationSeconds: 0.12,
  })

  try {
    await ensureContext()
    if (!state.engine) buildSources()
    state.started = true
    state.engine = true
    state.rpm = fingerprint.rpm
    state.throttle = fingerprint.throttle
    state.targetThrottle = fingerprint.throttle
    state.overrun = fingerprint.overrun
    state.shift = soundCheckScenario === 'shift' ? 0.72 : 0
    state.shiftDir = soundCheckScenario === 'shift' ? 1 : 0
    state.gear = state.gear || 1
    state.soundPreviewActive = true
    cluster.classList.add('live')
    refreshProfileParams()
    toast(SOUND_SCENARIO_LABELS[soundCheckScenario].toUpperCase(), model.engine.crankType.toUpperCase())
  } catch (e) {
    toast('AUDIO FEHLER', e.message)
    log.error('previewSoundScenario:', e.message)
    return
  }

  soundPreviewRestore = () => {
    if (!saved.engine) stopSources()
    Object.assign(state, saved)
    cluster.classList.toggle('live', saved.clusterLive)
    soundPreviewTimer = 0
    soundPreviewRestore = null
  }
  soundPreviewTimer = window.setTimeout(soundPreviewRestore, 1150)
}

/* --------------------------------------------------- Tacho-Cluster = Gas */

function clusterDown(e) {
  e.preventDefault()
  if (!state.started) {
    startAudio()
    return
  }
  state.manualHold = true
  state.targetThrottle = 1
  $('cluster').classList.add('gas')
}
function clusterUp() {
  if (!state.started || !state.manualHold) return
  state.manualHold = false
  state.targetThrottle = 0
  $('cluster').classList.remove('gas')
  if (state.engine && state.rpm > 1700) eventBoom('burble')
}

/* ----------------------------------------------------------------- Binding */

function applyMirror() {
  document.body.classList.toggle('hud-mirror', !!state.settings.mirror)
}

function bind() {
  document.querySelectorAll('.tab').forEach((b) => (b.onclick = () => setTab(b.dataset.t)))

  document.querySelectorAll('#soundScenarios button').forEach((button) => {
    button.onclick = () => {
      soundCheckScenario = button.dataset.scenario || 'idle'
      renderSoundCheck()
    }
  })
  const preview = $('soundPreview')
  if (preview) preview.onclick = previewSoundScenario

  document.querySelectorAll('.switch').forEach((sw) => {
    const k = sw.dataset.sw
    // Anfangszustand aus persistiertem State spiegeln
    const on = !!state.settings[k]
    sw.classList.toggle('on', on)
    sw.setAttribute('aria-checked', String(on))
    sw.onclick = () => {
      state.settings[k] = !state.settings[k]
      sw.classList.toggle('on', state.settings[k])
      sw.setAttribute('aria-checked', String(state.settings[k]))
      persist()
      if (k === 'mirror') applyMirror()
      if (k === 'debugTelemetry') renderDebugPanel()
    }
  })

  document.querySelectorAll('#mixPresets button').forEach((button) => {
    button.onclick = () => {
      applyMixPreset(button.dataset.preset || 'car')
      renderMixPresetButtons()
      toast('OUTPUT', (button.textContent || 'Car').toUpperCase())
    }
  })

  const cluster = $('cluster')
  cluster.addEventListener('pointerdown', clusterDown)
  ;['pointerup', 'pointercancel', 'pointerleave'].forEach((ev) => cluster.addEventListener(ev, clusterUp))

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && state.ctx) state.ctx.resume()
  })
}

function renderMixPresetButtons() {
  document.querySelectorAll('#mixPresets button').forEach((button) => {
    const on = button.dataset.preset === state.activeMixPreset
    button.classList.toggle('on', on)
    button.setAttribute('aria-pressed', String(on))
  })
}

function setTab(t) {
  document.querySelectorAll('.screen').forEach((s) => s.classList.remove('active'))
  $('s-' + t).classList.add('active')
  document.querySelectorAll('.tab').forEach((b) => b.classList.toggle('on', b.dataset.t === t))
  document.body.dataset.tab = t
}

/* ------------------------------------------------------------ Anzeige-Loop */

function arcPath(cx, cy, r, a1, a2) {
  const p = (a) => [cx + Math.cos((a * Math.PI) / 180) * r, cy + Math.sin((a * Math.PI) / 180) * r]
  const s = p(a1),
    e = p(a2)
  return `M ${s[0]} ${s[1]} A ${r} ${r} 0 ${a2 - a1 > 180 ? 1 : 0} 1 ${e[0]} ${e[1]}`
}

function set(id, v) {
  const el = $(id)
  if (el) el.textContent = v
}

function updateUi() {
  try {
    const red = redlineRpm()
    const rn = clamp(state.rpm / red, 0, 1)

    set('rpm', Math.round(state.rpm))
    set('speed', Math.round(state.speed))
    set('gear', state.gear || 'N')
    set('thr', Math.round(state.throttle * 100) + '%')

    state.load = smooth(
      state.load,
      state.throttle * (1 - state.shift * 0.42) + clamp(state.sensorAccel || 0, 0, 1) * 0.2,
      0.12,
    )
    set('load', Math.round(state.load * 100) + '%')

    set('sensorLive', (state.gSensor || 0).toFixed(2))
    set('latLive', (state.lateralG || 0).toFixed(2))
    set('accelLive', Math.round((state.sensorAccel || 0) * 100) + '%')
    set('overrunLive', Math.round((state.overrun || 0) * 100) + '%')
    set('rpmDrop', state.rpmDrop || 0)
    set('confLive', Math.round((state.sensorConfidence || 0) * 100) + '%')

    let phase = 'PULL',
      cls = ''
    if (state.shift > 0.08 && state.shiftDir > 0) {
      phase = 'UPSHIFT'
      cls = 'cut'
    } else if (state.shift > 0.08 && state.shiftDir < 0) {
      phase = 'DOWNSHIFT'
      cls = 'down'
    } else if ((state.overrun || 0) > 0.14) {
      phase = 'OFFLOAD'
      cls = 'down'
    } else if (state.sensor && state.sensorAccel > 0.35) {
      phase = 'G-PUNCH'
      cls = 'cut'
    } else if (!state.started) {
      phase = 'IDLE'
    }
    const gs = $('gearState')
    if (gs) {
      gs.textContent = phase
      gs.className = 'hudPhase shiftPhase ' + cls
    }

    // Nadel zeigt im Ruhezustand nach oben → Rotation = -120°..+120° über den Sweep.
    const needleRot = -120 + rn * GA_SWEEP
    const needle = $('needle')
    if (needle) needle.setAttribute('transform', `rotate(${needleRot},${CX},${CY})`)
    // Farbiger Füllbogen über denselben Bereich wie der Track (150° → …).
    const arc = $('arc')
    if (arc) arc.setAttribute('d', arcPath(CX, CY, 112, GA_START, GA_START + rn * GA_SWEEP))

    const sl = $('shiftlights')
    if (sl && sl.children.length) {
      const active = Math.round(rn * 12)
      ;[...sl.children].forEach((el, i) => el.classList.toggle('on', i < active))
    }
    renderDebugPanel()
  } catch {}
}

function renderDebugPanel() {
  const panel = $('debugPanel')
  if (!panel) return
  panel.classList.toggle('hidden', !state.settings.debugTelemetry)
  if (!state.settings.debugTelemetry) return

  const calibration = state.sensorCalibration?.status || 'off'
  panel.textContent = [
    `RPM ${Math.round(state.rpm)} | Speed ${Math.round(state.speed)} km/h | Gear ${state.gear || 'N'}`,
    `Throttle ${Math.round(state.throttle * 100)}% | Load ${Math.round(state.load * 100)}% | Overrun ${Math.round((state.overrun || 0) * 100)}%`,
    `Sensor ${state.sensor ? 'on' : 'off'} | Axis ${state.sensorAxis || '-'} | Cal ${calibration} | GPS ${state.gpsActive ? Math.round(state.gpsSpeed) + ' km/h' : 'off'}`,
    `GPS Src ${state.gpsSpeedSource || 'none'} | Age ${Number(state.gpsAgeSec || 0).toFixed(1)}s | Lead ${Math.round(state.gpsLeadKmh || 0)} km/h | Acc ${Math.round(state.gpsAccuracy || 0)}m`,
    `Perm Motion ${state.motionPermission || 'unknown'} | GPS ${state.gpsPermission || 'unknown'} | Issue ${state.sensorPermissionIssue || '-'}`,
    `G ${Number(state.gSensor || 0).toFixed(2)} | Lat ${Number(state.lateralG || 0).toFixed(2)} | Jerk ${Number(state.sensorJerk || 0).toFixed(2)}`,
    `Audio ${state.audioMode} | Preset ${state.activeMixPreset || 'custom'} | Profile ${state.profile?.id || '-'}`,
  ].join('\n')
}

/* ------------------------------------------------------------------- Loop */

function loop() {
  try {
    const t = performance.now()
    const dt = Math.min(0.05, (t - state.lastT) / 1000)
    state.lastT = t
    stepPhysics(dt)
    updateAudio()
    updateUi()
  } catch (e) {
    log.error('loop:', e.message)
  }
  requestAnimationFrame(loop)
}

/* --------------------------------------------------------------- Bootstrap */

export function initUi() {
  renderCards()
  renderSoundCheck()
  renderTicks()
  renderShiftlights()
  bind()
  applyMirror()
  renderMixPresetButtons()
  renderDebugPanel()
  selectProfile(state.activeProfileId)
  setTab('drive')
  requestAnimationFrame(loop)
}
